import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useDropzone, Accept } from 'react-dropzone';
import { X, Paperclip, FolderInput, ArrowLeft, FileUp, Loader } from 'lucide-react';

import { UPLOAD_SIZE_LIMIT } from 'src/shared/constants';
import { formatBytes } from 'src/utils/formatBytes';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { type AssetContentTypeEnum } from 'src/graphql/schema';

type RepoUploadProgress = { percent: number; currentCount: number };

type RepoUploadFailureResult = { status: 'canceled' } | { status: 'error'; message: string };

type RepoUploaderProps = {
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  back?: () => void;
  contentType: AssetContentTypeEnum;
  accept?: Accept;
  repoName?: string;
  assetId?: string;
  disableClick?: boolean;
  onDiscard?: () => void;
  uploadFiles: (
    files: File[],
    contentType: AssetContentTypeEnum,
    repoName?: string,
    assetId?: string,
  ) => Promise<Record<string, string | null> | undefined>;
  cancelUpload: () => void;
  uploadProgress: RepoUploadProgress;
  isVerifying: boolean;
  startVerification: (assetId: string) => void;
  stopVerification: () => void;
  onUploadFailure: (result: RepoUploadFailureResult) => void;
  onFileRejected: (message: string) => void;
};

const UploadProgress = ({
  uploadProgress,
  isVerifying,
  onDiscard,
}: {
  uploadProgress: RepoUploadProgress;
  isVerifying: boolean;
  onDiscard: () => void;
}) => {
  return (
    <div className="space-y-6">
      <div className="border border-border border-dashed bg-white rounded-lg p-6 min-h-[192px] flex items-center justify-center">
        <div className="w-[400px] max-w-full text-center space-y-2.5">
          {isVerifying ? (
            <Loader className="size-9 mx-auto animate-[spin_2s_linear_infinite]" />
          ) : (
            <FileUp className="size-9 mx-auto" />
          )}
          <Progress value={isVerifying ? 100 : uploadProgress.percent} className="h-4" />
          <span className="text-sm text-foreground font-semibold">
            {isVerifying ? 'Verifying repository format' : 'Uploading ZIP'}
          </span>
        </div>
      </div>
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onDiscard}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const formattedSizeLimit = formatBytes(UPLOAD_SIZE_LIMIT, 0);

export function RepoUploader({
  isUploading,
  setIsUploading,
  back,
  contentType,
  accept,
  repoName,
  assetId,
  disableClick,
  onDiscard: onDiscardProp,
  uploadFiles,
  cancelUpload,
  uploadProgress,
  isVerifying,
  startVerification,
  stopVerification,
  onUploadFailure,
  onFileRejected,
}: RepoUploaderProps) {
  const [files, setFiles] = useState<File[]>([]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => setFiles((files: File[]) => [...files, ...acceptedFiles].slice(0, 5)),
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0].errors[0];
      let message = error.message;

      if (error.code === 'file-too-large') {
        message = `File size exceeds the limit of ${formattedSizeLimit}. Please upload a smaller file.`;
      }

      onFileRejected(message);
    },
    maxFiles: 1,
    maxSize: UPLOAD_SIZE_LIMIT,
    accept,
    noClick: disableClick,
  });

  const uploadSelectedFiles = async () => {
    if (isUploading) return;

    setIsUploading(true);

    try {
      const uploadedAssetIds = await uploadFiles(files, contentType, repoName, assetId);
      setFiles([]);

      Object.values(uploadedAssetIds || {}).forEach((assetId) => {
        if (assetId) startVerification(assetId);
      });
    } catch (e) {
      if ((e as Error).name === 'CanceledError') {
        Sentry.captureException(e);
        onUploadFailure({ status: 'canceled' });
      } else {
        onUploadFailure({ status: 'error', message: (e as Error).message });
      }
      setIsUploading(false);
    }
  };

  const onDiscard = () => {
    setIsUploading(false);
    stopVerification();
    cancelUpload();
    setFiles([]);
    onDiscardProp?.();
  };

  const removeFile = (file: File) => setFiles((prevFiles) => prevFiles.filter(({ name }) => name !== file.name));

  if (isUploading || isVerifying) {
    return <UploadProgress uploadProgress={uploadProgress} isVerifying={isVerifying} onDiscard={onDiscard} />;
  }

  return (
    <section className="min-w-0">
      <div className="space-y-6 px-6 mb-6">
        {!files.length && (
          <>
            <div
              {...getRootProps()}
              className="px-6 py-10 border border-border border-dashed bg-muted/60 rounded-lg flex flex-col items-center cursor-pointer hover:bg-muted/100 transition-colors"
            >
              <input {...getInputProps()} />
              <FolderInput className="size-9 mb-2.5" />
              <p className="text-sm font-semibold mb-1.5">Drag and drop your ZIP file</p>
              <span className="text-sm text-muted-foreground mb-2.5">or</span>
              <Button size="sm">Browse files</Button>
            </div>
          </>
        )}
        {Boolean(files.length) && (
          <aside>
            <ul className="space-y-4">
              {files.map((file) => {
                const handleRemove = () => removeFile(file);

                return (
                  <li key={file.name} className="flex items-center gap-4 px-4 py-1.5 bg-accent rounded-md">
                    <Paperclip className="size-5 shrink-0" />
                    <div className="grow text-foreground text-sm truncate">{file.name}</div>
                    <Button className="shrink-0" variant="ghost" size="icon" onClick={handleRemove}>
                      <X className="size-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </aside>
        )}

        <p className="italic text-sm text-muted-foreground">You can upload 1 file ({formattedSizeLimit} max).</p>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={back}>
          <ArrowLeft />
          Back
        </Button>
        <Button
          disabled={!files.length || (!assetId && !repoName)}
          onClick={uploadSelectedFiles}
          isLoading={isUploading}
        >
          Add
        </Button>
      </div>
    </section>
  );
}

export type { RepoUploaderProps, RepoUploadProgress, RepoUploadFailureResult };
